import { action, observable } from 'mobx'
import { validationContext } from 'validx'
import type { InventoryItemForProvider } from '@/features/inventory/InventoryItemForProvider'
import { Id } from '@/infra/id'
import { type JobScopeItem } from '@/features/jobs/Job'

/**
 * An inventory service item in a scope change request.
 */
export class ScopeChangeInventoryItemViewModel {
  /**
   * The inventory item version the job uses, or the latest one.
   */
  @observable accessor versionId: Id = Id.none

  /**
   * The item name.
   */
  @observable accessor name = ''

  /**
   * The item description.
   */
  @observable accessor description = ''

  /**
   * Whether the item is picked for the request.
   */
  @observable accessor selected = false

  /**
   * The new quantity of the item
   */
  @observable accessor newQuantity = 1

  /**
   * The current quantity of the item
   */
  @observable accessor currentQuantity = 0

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    newQuantity: [
      (opts) =>
        opts.value >= 1 || opts.value <= -1 || 'Quantity cannot be zero.',
    ],
  })

  constructor(
    item: InventoryItemForProvider,
    previousItems: JobScopeItem[],
    isSelected: boolean,
    private readonly _onSelectedChange: (
      item: ScopeChangeInventoryItemViewModel,
    ) => void,
  ) {
    const previousItem = previousItems.find(
      (c) => c.inventoryItemId === item.id,
    )

    this.versionId = previousItem?.inventoryItemVersionId ?? item.versionId
    this.name = previousItem?.name ?? item.name
    this.description = previousItem?.description ?? item.description
    this.currentQuantity = previousItem?.quantity ?? 0
    // The quantity editor starts at the job's current quantity, if any.
    this.newQuantity = previousItem?.quantity ?? 1
    this.selected = isSelected
  }

  /**
   * Sets the new quantity.
   */
  @action.bound
  setQuantity(value: number | null) {
    this.newQuantity = value ?? 1
  }

  /**
   * Picks or unpicks the item.
   */
  @action.bound
  toggle() {
    this.selected = !this.selected
    this._onSelectedChange(this)
  }
}
